// Kurs → Reiter „Moodle“: Links pflegen, Code für den Moodle-Textblock erzeugen, Vorschau
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDaten } from '../daten/kontext';
import type { Kurs } from '../daten/typen';
import { konfig } from '../konfig';
import { moodleDaten } from '../moodle/daten';
import { zeichne } from '../moodle/baustein';
import { BAUSTEIN_CODE } from '../moodle/bausteinCode';
import { Feld, Icon, Knopf } from '../ui/bausteine';

type Art = 'live' | 'statisch';

export function MoodleBaustein({ kurs }: { kurs: Kurs }) {
  const { stand, quelle, ausfuehren, fragen, melden } = useDaten();
  const [links, setLinks] = useState(kurs.moodle_links ?? []);
  const [art, setArt] = useState<Art>(konfig.supabaseUrl ? 'live' : 'statisch');
  const vorschau = useRef<HTMLDivElement>(null);
  const daten = useMemo(() => moodleDaten({ ...stand, kurse: stand.kurse.map(k => k.id === kurs.id ? { ...k, moodle_links: links } : k) }, kurs.id), [stand, kurs.id, links]);

  useEffect(() => { if (vorschau.current && daten) zeichne(vorschau.current, daten); }, [daten]);

  const elementId = `mm-stundenplan-${kurs.kennung.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const aufruf = art === 'live'
    ? `MeisterMindStundenplan.starte(document.getElementById(${JSON.stringify(elementId)}), ${JSON.stringify({
        url: konfig.supabaseUrl || 'https://IHR-PROJEKT.supabase.co', key: konfig.supabaseAnonKey || 'IHR-ANON-KEY', token: kurs.moodle_token, intervallSek: 120 })});`
    : `MeisterMindStundenplan.zeichne(document.getElementById(${JSON.stringify(elementId)}), ${JSON.stringify(daten)});`;
  const code = `<!-- MeisterMind · ${art === 'live' ? 'Live-Stundenplan' : 'Stundenplan (Momentaufnahme)'} ${kurs.kennung} -->
<div id="${elementId}" lang="de">Der Stundenplan wird geladen …</div>
<script>${BAUSTEIN_CODE.replace(/<\/script/gi, '<\\/script')}
${aufruf.replace(/<\/script/gi, '<\\/script')}</script>`;

  async function kopieren() {
    (window as any).__mmKopiert = Date.now();
    try { await navigator.clipboard.writeText(code); melden('Code kopiert – jetzt in Moodle einfügen'); }
    catch {
      const ta = document.getElementById('moodle-code') as HTMLTextAreaElement | null;
      ta?.focus(); ta?.select();
      melden('Bitte den markierten Code mit Strg+C kopieren.', 'info');
    }
  }

  return (
    <div className="formular moodle">
      <p>Der Stundenplan erscheint im Moodle-Kurs als Textblock und zeigt nur <strong>freigegebene</strong> Termine. Entfallene Termine
        bleiben mit ihrem Hinweis sichtbar, damit die Teilnehmenden die Verlegung sehen.</p>

      <h3>Knöpfe im Stundenplan</h3>
      <p className="leise klein">Zum Beispiel Auditorium Maximum, Gruppenräume oder Dokumentencenter. Der erste Knopf wird hervorgehoben.</p>
      {links.map((l, i) => (
        <div key={i} className="zeile umbruch moodle-link">
          <input id={`moodle-link-text-${i}`} aria-label="Beschriftung" value={l.text} placeholder="Beschriftung" onChange={e => setLinks(links.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} />
          <input id={`moodle-link-url-${i}`} aria-label="Adresse" type="url" value={l.url} placeholder="https://…" onChange={e => setLinks(links.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} />
          <Knopf art="leise" icon="muell" aria-label="Knopf entfernen" onClick={() => setLinks(links.filter((_, j) => j !== i))} />
        </div>
      ))}
      <div className="zeile umbruch">
        <Knopf icon="plus" onClick={() => setLinks([...links, { text: '', url: '' }])} disabled={links.length >= 4}>Knopf hinzufügen</Knopf>
        <Knopf art="primaer" icon="haken" onClick={() => {
          const sauber = links.filter(l => l.text.trim() && /^https?:\/\//.test(l.url.trim())).map(l => ({ text: l.text.trim(), url: l.url.trim() }));
          if (sauber.length !== links.length) melden('Knöpfe ohne Beschriftung oder ohne gültige Adresse (https://…) wurden entfernt.', 'info');
          setLinks(sauber);
          void ausfuehren(() => quelle.speichern('kurs', { id: kurs.id, moodle_links: sauber }), 'Knöpfe gespeichert');
        }}>Knöpfe speichern</Knopf>
      </div>

      <h3>Code für Moodle</h3>
      <div className="segment">
        <button type="button" className={art === 'live' ? 'an' : ''} onClick={() => setArt('live')}>Live (aktualisiert sich selbst)</button>
        <button type="button" className={art === 'statisch' ? 'an' : ''} onClick={() => setArt('statisch')}>Momentaufnahme</button>
      </div>
      {art === 'live' && !konfig.supabaseUrl && <div className="hinweisbox warn"><Icon name="warnung" />
        <span>In der Demo ist keine Datenbank angebunden. Im Code stehen deshalb Platzhalter für Projektadresse und Schlüssel – im echten Betrieb trägt das System sie selbst ein.</span></div>}
      {art === 'statisch' && <div className="hinweisbox info"><Icon name="kalender" />
        <span>Die Momentaufnahme enthält die Termine von jetzt und ändert sich nicht. Sie eignet sich zum Ausprobieren in Moodle. Für den Betrieb „Live“ verwenden.</span></div>}
      <textarea id="moodle-code" className="code" readOnly value={code} rows={6} onFocus={e => e.target.select()} aria-label="Code für Moodle" />
      <div className="zeile umbruch">
        <Knopf art="primaer" icon="liste" onClick={kopieren}>Code kopieren</Knopf>
        <span className="leise klein">{(code.length / 1024).toFixed(0)} KB · Kurs-Schlüssel {kurs.moodle_token.slice(0, 8)}…</span>
      </div>
      <ol className="anleitung klein">
        <li>In Moodle den Kurs öffnen und <strong>Bearbeiten einschalten</strong>.</li>
        <li>Im gewünschten Abschnitt einen <strong>Textbereich</strong> (oder die Abschnittsbeschreibung) bearbeiten.</li>
        <li>Im Editor die <strong>HTML-Ansicht</strong> öffnen (Symbol <code>&lt;/&gt;</code>), den Code einfügen und speichern.</li>
        <li>Skripte in Moodle-Texten setzen die Berechtigung „Vertrauenswürdige Inhalte“ voraus – die bisherigen Stundenplan-Blöcke nutzen sie bereits.</li>
      </ol>
      <div className="zeile umbruch">
        <Knopf art="leise" icon="zurueck" onClick={async () => {
          if (!await fragen('Neuen Kurs-Schlüssel erzeugen? Bereits in Moodle eingefügter Live-Code zeigt danach nichts mehr an und muss neu eingefügt werden.', 'Schlüssel erneuern', true)) return;
          const neu = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
          await ausfuehren(() => quelle.speichern('kurs', { id: kurs.id, moodle_token: neu }), 'Neuer Schlüssel erzeugt');
        }}>Schlüssel erneuern</Knopf>
        <span className="leise klein">nur nötig, wenn der Code an Unbefugte gelangt ist</span>
      </div>

      <h3>Vorschau</h3>
      <div className="moodle-vorschau"><div ref={vorschau} /></div>
    </div>
  );
}
